import { MXSelectionValueComponent, MXLookComponent, AXHotkeysService, convertArrayToDataSource, AX_SELECTION_DATA_TOKEN, AXComponent, AXFocusableComponent, AXValuableComponent, AXClearableComponent, AXClosbaleComponent, AXSearchableComponent, MXSelectionBridgeService, AXCommonModule } from '@acorex/components/common';
import * as i5 from '@acorex/components/dropdown';
import { MXDropdownBoxBaseComponent, AXDropdownBoxComponent, AXDropdownModule } from '@acorex/components/dropdown';
import * as i6 from '@acorex/components/list';
import { AXListComponent, AXListModule } from '@acorex/components/list';
import { AXSearchBoxComponent } from '@acorex/components/search-box';
import { AXUnsubscriber } from '@acorex/core/utils';
import * as i0 from '@angular/core';
import { inject, signal, forwardRef, Component, ChangeDetectionStrategy, ViewEncapsulation, Input, ViewChild, ContentChild, HostListener, NgModule } from '@angular/core';
import * as i2 from '@angular/forms';
import { NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { last, findLastIndex, nth } from 'lodash-es';
import { classes } from 'polytype';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i3 from '@acorex/components/decorators';
import { AXDecoratorModule } from '@acorex/components/decorators';
import * as i4 from '@acorex/components/loading';
import { AXLoadingModule } from '@acorex/components/loading';
import * as i7 from '@acorex/core/translation';
import { AXTranslationModule } from '@acorex/core/translation';
import { AXBadgeModule } from '@acorex/components/badge';
import { AXCheckBoxModule } from '@acorex/components/check-box';
import { AXPopoverModule } from '@acorex/components/popover';
import { A11yModule } from '@angular/cdk/a11y';
import { AXTextBoxModule } from '@acorex/components/text-box';

/**
 * The Button is a component which detects user interaction and triggers a corresponding event
 *
 * @category Components
 */
class AXSelectBoxComponent extends classes(MXDropdownBoxBaseComponent, MXSelectionValueComponent, MXLookComponent) {
    constructor() {
        super(...arguments);
        this.hotKeyService = inject(AXHotkeysService);
        this.isLoading = signal(false);
        this.renderList = false;
        this.dropdownSizes = {
            width: '100%',
            height: 'auto',
        };
        this._listDataSource = convertArrayToDataSource([], {
            key: this.valueField,
            pageSize: 10,
        });
        this.dropdownWidth = 260;
        this.autoHeight = false;
        this.selectionService = inject(AX_SELECTION_DATA_TOKEN);
        this._unsubscriber = inject(AXUnsubscriber);
    }
    get dataSource() {
        return this._dataSource;
    }
    set dataSource(v) {
        this._dataSource = v;
        if (Array.isArray(v)) {
            this._listDataSource = convertArrayToDataSource(v, {
                key: this.valueField,
                pageSize: 10,
            });
        }
        else {
            this._listDataSource = this.dataSource;
        }
        //
        //
        this._listDataSource.onChanged.pipe(this._unsubscriber.takeUntilDestroy).subscribe((data) => {
            this.setDropdownSize(data.totalCount);
        });
        this._listDataSource.onLoadingChanged.pipe(this._unsubscriber.takeUntilDestroy).subscribe((loading) => {
            this.isLoading.set(loading);
        });
    }
    ngOnInit() {
        super.ngOnInit();
        //
        this.registerValidation();
    }
    ngAfterViewInit() {
        this.setDropdownSize();
    }
    ngOnDestroy() {
        this.searchBox?.onKeyDown.unsubscribe();
    }
    getItemByKey(key) {
        const startTime = Date.now();
        const check = async () => {
            while (Date.now() - startTime < 2000) {
                if (typeof this._listDataSource.find === 'function') {
                    return this._listDataSource.find(key);
                }
                else {
                    await new Promise((resolve) => setTimeout(resolve, 50));
                }
            }
            console.log('Timeout reached without finding the "getItemByKey" method');
        };
        return check();
    }
    _handleOnOpenedEvent(e) {
        this.renderList = true;
        this.list?.render();
        this._handleFocus();
        //
        this.onOpened.emit({
            component: this,
            isUserInteraction: e.isUserInteraction,
        });
    }
    _handleOnClosedEvent(e) {
        //this.input.focus();
        this.onClosed.emit({
            component: this,
            isUserInteraction: e.isUserInteraction,
        });
        this._unsubscriber.unsubscribe();
    }
    _handleFocus() {
        setTimeout(() => {
            if (this.searchBox) {
                this.searchBox.focus();
                //TODO: unsubscribe
                this.hotKeyService
                    .addShortcut({ keys: 'Control.f', element: this.panel.nativeElement })
                    .pipe(this._unsubscriber.takeUntilDestroy)
                    .subscribe(() => {
                    this.searchBox.focus();
                });
                this.searchBox.onKeyDown.pipe(this._unsubscriber.takeUntilDestroy).subscribe((e) => {
                    if (e.nativeEvent.code === 'ArrowDown' || e.nativeEvent.key === 'ArrowDown') {
                        this.list?.focus();
                        e.nativeEvent.preventDefault();
                    }
                });
            }
            else {
                this.list?.focus();
            }
        });
    }
    _handleBadgeRemove(e, item) {
        this.unselectItems(item);
        e.stopPropagation();
    }
    _handleValueChanged(e) {
        if (e.isUserInteraction) {
            this.commitValue(e.component.selectedItems, true);
        }
    }
    _handleOnItemClick(e) {
        if (!this.multiple) {
            this.close();
        }
    }
    internalValueChanged() {
        if (!this.multiple)
            this.close();
        setTimeout(() => {
            this.detectAutoHeight();
        }, 100);
    }
    detectAutoHeight() {
        const containerWidth = this.getHostElement().querySelector('.ax-select-box-selection').clientWidth;
        const itemsWidth = Array.from(this.getHostElement().querySelectorAll('.ax-selected-token')).reduce((a, i) => a + i.clientWidth, 0);
        this.autoHeight = containerWidth - itemsWidth <= 8;
        this.dropdown.updatePosition();
        this.cdr.markForCheck();
    }
    setDropdownSize(count = 0) {
        if (this.dropdown.isActionsheetStyle) {
            this.dropdownSizes = {
                width: '100%',
                height: ['auto', '0px'].includes(this.dropdownSizes.height) ? `${Math.min(15, count) * 40}px` : this.dropdownSizes.height,
            };
        }
        else {
            //TODO: calc min-with from formula or config
            const hostWidth = Math.max(this.getHostElement().offsetWidth, this.dropdownWidth);
            this.dropdownSizes = {
                width: `${hostWidth}px`,
                height: count == 0 ? 'auto' : `${Math.min(5, count) * 40}px`,
            };
        }
        setTimeout(() => {
            this.dropdown.updatePosition();
        });
    }
    _handleKeydown(e) {
        if (e.code === 'ArrowDown' || e.code === 'ArrowUp') {
            this.selectItemByNav(e.code === 'ArrowDown' ? 1 : -1);
            e.preventDefault();
        }
        else if (e.code === 'Backspace') {
            this.unselectItems(this.selectedItems.pop());
            e.preventDefault();
        }
        // if ((e.code === 'Space' || e.code === 'Enter') && this.hasItems) {
        //     if (this.readonly || this.disabled) {
        //         e.preventDefault();
        //         e.stopPropagation();
        //         return;
        //     }
        //     const id = document.activeElement?.closest('li')?.dataset?.id;
        //     this.toggleSelect(id);
        //     e.preventDefault();
        //     e.stopPropagation()
        // }
    }
    selectItemByNav(sign) {
        if (Array.isArray(this.dataSource) && !this.multiple) {
            const items = this.normalizeItemsList(this.dataSource);
            const _last = last(this.selectedItems);
            let i = -1;
            if (_last) {
                i = findLastIndex(items, [this.valueField, _last[this.valueField]]);
            }
            i += sign;
            if (i < 0 || i >= items.length)
                return;
            const next = nth(items, i);
            if (next) {
                this.selectItems(next);
            }
        }
        else {
            this.open();
        }
    }
    search(term) {
        if (term) {
            this._listDataSource.filter({
                field: this.textField,
                value: term,
                operator: { type: 'contains' },
            });
        }
        else {
            this._listDataSource.clearFilter();
        }
        this._listDataSource.refresh();
    }
    refresh() {
        this.reset(false);
        this.clearSelectionCache();
        this.list?.refresh();
        this.close();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXSelectBoxComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.0.2", type: AXSelectBoxComponent, selector: "ax-select-box", inputs: { disabled: "disabled", readonly: "readonly", tabIndex: "tabIndex", placeholder: "placeholder", minValue: "minValue", maxValue: "maxValue", value: "value", state: "state", name: "name", id: "id", type: "type", look: "look", multiple: "multiple", valueField: "valueField", textField: "textField", textTemplate: "textTemplate", dataSource: "dataSource", caption: "caption", itemTemplate: "itemTemplate", selectedTemplate: "selectedTemplate", emptyTemplate: "emptyTemplate", loadingTemplate: "loadingTemplate", dropdownWidth: "dropdownWidth" }, outputs: { valueChange: "valueChange", stateChange: "stateChange", onValueChanged: "onValueChanged", onBlur: "onBlur", onFocus: "onFocus", readonlyChange: "readonlyChange", disabledChange: "disabledChange", onOpened: "onOpened", onClosed: "onClosed" }, host: { attributes: { "ngSkipHydration": "true" }, listeners: { "keydown": "_handleKeydown($event)" } }, providers: [
            { provide: AXComponent, useExisting: AXSelectBoxComponent },
            { provide: AXFocusableComponent, useExisting: AXSelectBoxComponent },
            { provide: AXValuableComponent, useExisting: AXSelectBoxComponent },
            { provide: AXClearableComponent, useExisting: AXSelectBoxComponent },
            { provide: AXClosbaleComponent, useExisting: AXSelectBoxComponent },
            { provide: AXSearchableComponent, useExisting: AXSelectBoxComponent },
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AXSelectBoxComponent),
                multi: true,
            },
            {
                provide: AX_SELECTION_DATA_TOKEN,
                useClass: MXSelectionBridgeService,
            },
            AXUnsubscriber,
        ], queries: [{ propertyName: "searchBox", first: true, predicate: AXSearchBoxComponent, descendants: true, static: true }], viewQueries: [{ propertyName: "panel", first: true, predicate: ["panel"], descendants: true }, { propertyName: "list", first: true, predicate: AXListComponent, descendants: true }, { propertyName: "dropdown", first: true, predicate: AXDropdownBoxComponent, descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<ax-dropdown-box\n  [disabled]=\"disabled\"\n  (onOpened)=\"_handleOnOpenedEvent($event)\"\n  (onClosed)=\"_handleOnClosedEvent($event)\"\n  [look]=\"look\"\n  [class.ax-auto-height]=\"autoHeight\"\n  (focus)=\"emitOnFocusEvent($event)\"\n  (blur)=\"emitOnBlurEvent($event)\"\n>\n  <ng-container input>\n    <ng-content select=\"ax-prefix\"> </ng-content>\n    <div class=\"ax-select-box-selection ax-content\" [class.ax-multiple]=\"multiple\" [tabindex]=\"tabIndex\" (click)=\"toggle()\">\n      @if (selectedItems.length === 0) {\n        <div class=\"ax-placeholder\" role=\"textbox\" area-readonly=\"true\">\n          {{ placeholder }}\n        </div>\n      }\n      @for (item of selectedItems; track $index) {\n        @if (selectedTemplate) {\n          <ng-template *ngTemplateOutlet=\"selectedTemplate; context: { $implicit: { data: item } }\"></ng-template>\n        } @else {\n          <div class=\"ax-selected-token\">\n            {{ getDisplayText(item) }}\n            @if (!disabled && !readonly && multiple) {\n              <span class=\"ax-icon ax-icon-close\" (click)=\"_handleBadgeRemove($event, item)\"> </span>\n            }\n          </div>\n        }\n      }\n    </div>\n    @if (selectedItems?.length && !disabled && !readonly) {\n      <ng-content select=\"ax-clear-button\"></ng-content>\n    }\n    <button type=\"button\" [disabled]=\"disabled\" [tabIndex]=\"-1\" class=\"ax-general-button ax-button-icon\" (click)=\"toggle()\">\n      <!-- @if (isLoading() && !isOpen) {\n        <ax-loading type=\"spinner\"></ax-loading>\n      } @else { -->\n      <span\n        class=\"ax-icon ax-icon-chevron-left ax-arrow-button\"\n        [ngClass]=\"{\n          '-rotation-90': !isOpen,\n          'rotation-90': isOpen,\n        }\"\n      ></span>\n      <!-- } -->\n    </button>\n    <ng-content select=\"ax-suffix\"> </ng-content>\n    <ng-template #search>\n      <ng-content select=\"ax-search-box\"> </ng-content>\n    </ng-template>\n  </ng-container>\n  <ng-container panel>\n    <div #panel class=\"ax-select-box-panel\" [style.min-width]=\"dropdownSizes.width\">\n      @if (dropdown.isActionsheetStyle) {\n        <ax-header class=\"ax-solid\">\n          <ax-title>{{ caption || placeholder || 'selectbox.popover.title' | translate | async }}</ax-title>\n          <ax-close-button [icon]=\"multiple ? 'ax-icon ax-icon-check !ax-text-primary-500' : 'ax-icon ax-icon-close'\"></ax-close-button>\n        </ax-header>\n      }\n      @if (searchBox) {\n        <div class=\"ax-search-container\">\n          <ng-template [ngTemplateOutlet]=\"search\"></ng-template>\n        </div>\n      }\n      @if (renderList) {\n        <ax-list\n          [dataSource]=\"_listDataSource\"\n          [multiple]=\"multiple\"\n          [style.height]=\"dropdownSizes.height\"\n          [valueField]=\"valueField\"\n          [textField]=\"textField\"\n          [textTemplate]=\"textTemplate\"\n          [emptyTemplate]=\"emptyTemplate ?? empty\"\n          [itemTemplate]=\"itemTemplate\"\n          [loadingTemplate]=\"loadingTemplate\"\n          [ngModel]=\"value\"\n          (onValueChanged)=\"_handleValueChanged($event)\"\n          [selectionMode]=\"'item'\"\n          (onItemClick)=\"_handleOnItemClick($event)\"\n        >\n          <ng-template #empty> {{ 'no-result-found' | translate | async }} </ng-template>\n        </ax-list>\n      }\n\n      @if (isLoading()) {\n        @if (loadingTemplate) {\n          <ng-template *ngTemplateOutlet=\"loadingTemplate\"></ng-template>\n        } @else {\n          <div class=\"ax-flex ax-items-center ax-justify-center ax-p-4\">\n            <ax-loading></ax-loading>\n          </div>\n        }\n      }\n\n      <ng-content select=\"ax-footer\"> </ng-content>\n    </div>\n  </ng-container>\n</ax-dropdown-box>\n<ng-content select=\"ax-validation-rule\"> </ng-content>\n", styles: ["ax-select-box{display:block;width:100%}ax-select-box ax-dropdown-box.ax-auto-height{height:auto!important}ax-select-box .ax-editor-container.ax-look-fill .ax-select-box-selection.ax-multiple .ax-selected-token{background-color:rgba(var(--ax-color-surface))}ax-select-box .ax-selected-token{display:flex;align-items:center;border-radius:var(--ax-rounded-border-default);padding:.25rem .5rem;color:rgb(var(--ax-color-text-default))}ax-select-box .ax-selected-token .ax-icon-close{margin-inline-start:.5rem;cursor:pointer;width:1rem;height:1rem}ax-select-box .ax-select-box-selection{display:flex;flex:1 1 0%;cursor:pointer;-webkit-user-select:none;user-select:none;flex-wrap:wrap;align-items:center;justify-content:flex-start;gap:.25rem;padding:.25rem;outline:2px solid transparent;outline-offset:2px;height:100%}ax-select-box .ax-select-box-selection>span{white-space:nowrap;padding-inline-end:.75rem;padding-inline-start:1rem}ax-select-box .ax-select-box-selection .ax-selectbox-input{width:0px;opacity:0}ax-select-box .ax-select-box-selection.ax-multiple .ax-selected-token{background-color:rgba(var(--ax-color-on-surface))}ax-select-box .ax-placeholder{padding-inline-end:.75rem;padding-inline-start:1rem}ax-select-box .ax-general-button .ax-arrow-button{transition-property:all;transition-timing-function:cubic-bezier(.4,0,.2,1);transition-duration:.15s}ax-select-box .ax-general-button .ax-arrow-button.-rotation-90{transform:rotate(-90deg)}ax-select-box .ax-general-button .ax-arrow-button.rotation-90{transform:rotate(90deg)}.ax-select-box-panel{height:fit-content}.ax-select-box-panel>ax-header.ax-solid{border-bottom-width:1px;border-color:rgba(var(--ax-color-border-default))}.ax-select-box-panel>ax-header.ax-solid ax-title{font-size:1rem;line-height:1.5rem;font-weight:500}.ax-select-box-panel .ax-search-container{padding:.5rem}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: i3.AXDecoratorCloseButtonComponent, selector: "ax-close-button", inputs: ["icon"] }, { kind: "component", type: i3.AXDecoratorGenericComponent, selector: "ax-footer, ax-header, ax-content, ax-divider, ax-form-hint, ax-prefix, ax-suffix, ax-text, ax-title, ax-sub-title, ax-placeholder, ax-overlay" }, { kind: "component", type: i4.AXLoadingComponent, selector: "ax-loading", inputs: ["visible", "type", "context"], outputs: ["visibleChange"] }, { kind: "component", type: i5.AXDropdownBoxComponent, selector: "ax-dropdown-box", inputs: ["disabled", "look"], outputs: ["disabledChange", "onBlur", "onFocus", "onClick", "onOpened", "onClosed"] }, { kind: "component", type: i6.AXListComponent, selector: "ax-list", inputs: ["id", "name", "disabled", "readonly", "valueField", "textField", "textTemplate", "disabledField", "multiple", "selectionMode", "dataSource", "itemHeight", "itemTemplate", "emptyTemplate", "loadingTemplate", "checkbox"], outputs: ["onValueChanged", "disabledChange", "readOnlyChange", "onBlur", "onFocus", "onItemClick", "onScrolledIndexChanged"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i7.AXTranslatorPipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXSelectBoxComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ax-select-box', inputs: [
                        'disabled',
                        'readonly',
                        'tabIndex',
                        'placeholder',
                        'minValue',
                        'maxValue',
                        'value',
                        'state',
                        'name',
                        'id',
                        'type',
                        'look',
                        'multiple',
                        'valueField',
                        'textField',
                        'textTemplate',
                    ], outputs: [
                        'valueChange',
                        'stateChange',
                        'onValueChanged',
                        'onBlur',
                        'onFocus',
                        'readonlyChange',
                        'disabledChange',
                        'onOpened',
                        'onClosed',
                    ], changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, providers: [
                        { provide: AXComponent, useExisting: AXSelectBoxComponent },
                        { provide: AXFocusableComponent, useExisting: AXSelectBoxComponent },
                        { provide: AXValuableComponent, useExisting: AXSelectBoxComponent },
                        { provide: AXClearableComponent, useExisting: AXSelectBoxComponent },
                        { provide: AXClosbaleComponent, useExisting: AXSelectBoxComponent },
                        { provide: AXSearchableComponent, useExisting: AXSelectBoxComponent },
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AXSelectBoxComponent),
                            multi: true,
                        },
                        {
                            provide: AX_SELECTION_DATA_TOKEN,
                            useClass: MXSelectionBridgeService,
                        },
                        AXUnsubscriber,
                    ], host: { ngSkipHydration: 'true' }, template: "<ax-dropdown-box\n  [disabled]=\"disabled\"\n  (onOpened)=\"_handleOnOpenedEvent($event)\"\n  (onClosed)=\"_handleOnClosedEvent($event)\"\n  [look]=\"look\"\n  [class.ax-auto-height]=\"autoHeight\"\n  (focus)=\"emitOnFocusEvent($event)\"\n  (blur)=\"emitOnBlurEvent($event)\"\n>\n  <ng-container input>\n    <ng-content select=\"ax-prefix\"> </ng-content>\n    <div class=\"ax-select-box-selection ax-content\" [class.ax-multiple]=\"multiple\" [tabindex]=\"tabIndex\" (click)=\"toggle()\">\n      @if (selectedItems.length === 0) {\n        <div class=\"ax-placeholder\" role=\"textbox\" area-readonly=\"true\">\n          {{ placeholder }}\n        </div>\n      }\n      @for (item of selectedItems; track $index) {\n        @if (selectedTemplate) {\n          <ng-template *ngTemplateOutlet=\"selectedTemplate; context: { $implicit: { data: item } }\"></ng-template>\n        } @else {\n          <div class=\"ax-selected-token\">\n            {{ getDisplayText(item) }}\n            @if (!disabled && !readonly && multiple) {\n              <span class=\"ax-icon ax-icon-close\" (click)=\"_handleBadgeRemove($event, item)\"> </span>\n            }\n          </div>\n        }\n      }\n    </div>\n    @if (selectedItems?.length && !disabled && !readonly) {\n      <ng-content select=\"ax-clear-button\"></ng-content>\n    }\n    <button type=\"button\" [disabled]=\"disabled\" [tabIndex]=\"-1\" class=\"ax-general-button ax-button-icon\" (click)=\"toggle()\">\n      <!-- @if (isLoading() && !isOpen) {\n        <ax-loading type=\"spinner\"></ax-loading>\n      } @else { -->\n      <span\n        class=\"ax-icon ax-icon-chevron-left ax-arrow-button\"\n        [ngClass]=\"{\n          '-rotation-90': !isOpen,\n          'rotation-90': isOpen,\n        }\"\n      ></span>\n      <!-- } -->\n    </button>\n    <ng-content select=\"ax-suffix\"> </ng-content>\n    <ng-template #search>\n      <ng-content select=\"ax-search-box\"> </ng-content>\n    </ng-template>\n  </ng-container>\n  <ng-container panel>\n    <div #panel class=\"ax-select-box-panel\" [style.min-width]=\"dropdownSizes.width\">\n      @if (dropdown.isActionsheetStyle) {\n        <ax-header class=\"ax-solid\">\n          <ax-title>{{ caption || placeholder || 'selectbox.popover.title' | translate | async }}</ax-title>\n          <ax-close-button [icon]=\"multiple ? 'ax-icon ax-icon-check !ax-text-primary-500' : 'ax-icon ax-icon-close'\"></ax-close-button>\n        </ax-header>\n      }\n      @if (searchBox) {\n        <div class=\"ax-search-container\">\n          <ng-template [ngTemplateOutlet]=\"search\"></ng-template>\n        </div>\n      }\n      @if (renderList) {\n        <ax-list\n          [dataSource]=\"_listDataSource\"\n          [multiple]=\"multiple\"\n          [style.height]=\"dropdownSizes.height\"\n          [valueField]=\"valueField\"\n          [textField]=\"textField\"\n          [textTemplate]=\"textTemplate\"\n          [emptyTemplate]=\"emptyTemplate ?? empty\"\n          [itemTemplate]=\"itemTemplate\"\n          [loadingTemplate]=\"loadingTemplate\"\n          [ngModel]=\"value\"\n          (onValueChanged)=\"_handleValueChanged($event)\"\n          [selectionMode]=\"'item'\"\n          (onItemClick)=\"_handleOnItemClick($event)\"\n        >\n          <ng-template #empty> {{ 'no-result-found' | translate | async }} </ng-template>\n        </ax-list>\n      }\n\n      @if (isLoading()) {\n        @if (loadingTemplate) {\n          <ng-template *ngTemplateOutlet=\"loadingTemplate\"></ng-template>\n        } @else {\n          <div class=\"ax-flex ax-items-center ax-justify-center ax-p-4\">\n            <ax-loading></ax-loading>\n          </div>\n        }\n      }\n\n      <ng-content select=\"ax-footer\"> </ng-content>\n    </div>\n  </ng-container>\n</ax-dropdown-box>\n<ng-content select=\"ax-validation-rule\"> </ng-content>\n", styles: ["ax-select-box{display:block;width:100%}ax-select-box ax-dropdown-box.ax-auto-height{height:auto!important}ax-select-box .ax-editor-container.ax-look-fill .ax-select-box-selection.ax-multiple .ax-selected-token{background-color:rgba(var(--ax-color-surface))}ax-select-box .ax-selected-token{display:flex;align-items:center;border-radius:var(--ax-rounded-border-default);padding:.25rem .5rem;color:rgb(var(--ax-color-text-default))}ax-select-box .ax-selected-token .ax-icon-close{margin-inline-start:.5rem;cursor:pointer;width:1rem;height:1rem}ax-select-box .ax-select-box-selection{display:flex;flex:1 1 0%;cursor:pointer;-webkit-user-select:none;user-select:none;flex-wrap:wrap;align-items:center;justify-content:flex-start;gap:.25rem;padding:.25rem;outline:2px solid transparent;outline-offset:2px;height:100%}ax-select-box .ax-select-box-selection>span{white-space:nowrap;padding-inline-end:.75rem;padding-inline-start:1rem}ax-select-box .ax-select-box-selection .ax-selectbox-input{width:0px;opacity:0}ax-select-box .ax-select-box-selection.ax-multiple .ax-selected-token{background-color:rgba(var(--ax-color-on-surface))}ax-select-box .ax-placeholder{padding-inline-end:.75rem;padding-inline-start:1rem}ax-select-box .ax-general-button .ax-arrow-button{transition-property:all;transition-timing-function:cubic-bezier(.4,0,.2,1);transition-duration:.15s}ax-select-box .ax-general-button .ax-arrow-button.-rotation-90{transform:rotate(-90deg)}ax-select-box .ax-general-button .ax-arrow-button.rotation-90{transform:rotate(90deg)}.ax-select-box-panel{height:fit-content}.ax-select-box-panel>ax-header.ax-solid{border-bottom-width:1px;border-color:rgba(var(--ax-color-border-default))}.ax-select-box-panel>ax-header.ax-solid ax-title{font-size:1rem;line-height:1.5rem;font-weight:500}.ax-select-box-panel .ax-search-container{padding:.5rem}\n"] }]
        }], propDecorators: { dataSource: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], caption: [{
                type: Input
            }], itemTemplate: [{
                type: Input
            }], selectedTemplate: [{
                type: Input
            }], emptyTemplate: [{
                type: Input
            }], loadingTemplate: [{
                type: Input
            }], dropdownWidth: [{
                type: Input
            }], panel: [{
                type: ViewChild,
                args: ['panel']
            }], list: [{
                type: ViewChild,
                args: [AXListComponent]
            }], searchBox: [{
                type: ContentChild,
                args: [AXSearchBoxComponent, { static: true }]
            }], dropdown: [{
                type: ViewChild,
                args: [AXDropdownBoxComponent, { static: true }]
            }], _handleKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

class AXSelectBoxModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXSelectBoxModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.0.2", ngImport: i0, type: AXSelectBoxModule, declarations: [AXSelectBoxComponent], imports: [CommonModule,
            AXCommonModule,
            FormsModule,
            AXCheckBoxModule,
            AXBadgeModule,
            AXDecoratorModule,
            AXTranslationModule,
            AXPopoverModule,
            AXLoadingModule,
            A11yModule,
            AXTextBoxModule,
            AXDropdownModule,
            AXListModule], exports: [AXSelectBoxComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXSelectBoxModule, imports: [CommonModule,
            AXCommonModule,
            FormsModule,
            AXCheckBoxModule,
            AXBadgeModule,
            AXDecoratorModule,
            AXTranslationModule,
            AXPopoverModule,
            AXLoadingModule,
            A11yModule,
            AXTextBoxModule,
            AXDropdownModule,
            AXListModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXSelectBoxModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        AXCommonModule,
                        FormsModule,
                        AXCheckBoxModule,
                        AXBadgeModule,
                        AXDecoratorModule,
                        AXTranslationModule,
                        AXPopoverModule,
                        AXLoadingModule,
                        A11yModule,
                        AXTextBoxModule,
                        AXDropdownModule,
                        AXListModule,
                    ],
                    exports: [AXSelectBoxComponent],
                    declarations: [AXSelectBoxComponent],
                    providers: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AXSelectBoxComponent, AXSelectBoxModule };
